import React, { useState, useEffect } from 'react';

const StepNavigation = ({ steps, currentStep, onStepClick, errors, formData = {} }) => {
    const [isCollapsed, setIsCollapsed] = useState(true);
    const [isMobile, setIsMobile] = useState(false);

    // Detectar móvil sin auto-colapso/auto-expandir
    useEffect(() => {
        const checkMobile = () => {
            const mobile = window.innerWidth <= 768;
            setIsMobile(mobile);
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    const fieldIsEmpty = (value) => {
        if (Array.isArray(value)) return value.length === 0;
        return !value || String(value).trim() === '';
    };

    const stepHasErrors = (stepId) => {
        const type = formData?.type;
        const requiredByStep = {
            basic: ['name', 'description', ...(formData?.username !== undefined ? ['username'] : [])],
            documents: type === 'service' ? ['documentName'/*, 'documentPhoto'*/] : [],
            contact: ['phone', 'locations', ...(type === 'products' && formData?.cuit !== undefined ? ['cuit'] : [])],
            categories: ['categories'],
            media: ['photo']
        };

        const requiredFields = requiredByStep[stepId] || [];
        if (requiredFields.length === 0) return false;

        // Verificar errores presentes
        const errorPresent = Object.keys(errors || {}).some((k) => requiredFields.includes(k));
        if (errorPresent) return true;

        // Para el paso de documents en servicios, verificar lógica especial de DNI o CUIT
        if (stepId === 'documents' && type === 'service') {
            const hasDocument = !fieldIsEmpty(formData?.document);
            const hasCuit = !fieldIsEmpty(formData?.cuit);
            const hasDocumentName = !fieldIsEmpty(formData?.documentName);

            // Primero verificar documentName (siempre requerido)
            if (!hasDocumentName) return true;

            // Debe tener al menos uno: DNI o CUIT
            const hasEitherDocument = hasDocument || hasCuit;
            if (!hasEitherDocument) return true;

            // Si tiene documentName y al menos un documento válido, considerar completo
            // Los errores específicos de validación se manejan en tiempo real en el form
            return false;
        }

        return requiredFields.some((field) => fieldIsEmpty(formData?.[field]));
    };

    const getStepStatus = (stepIndex, stepId) => {
        if (stepIndex < currentStep) {
            const hasStepErr = stepHasErrors(stepId);
            return hasStepErr ? 'error' : 'completed';
        }
        if (stepIndex === currentStep) {
            return 'active';
        }
        return 'pending';
    };

    const toggleCollapse = () => {
        requestAnimationFrame(() => {
            try {
                setIsCollapsed(!isCollapsed);
            } catch (error) {
                console.warn("Error al cambiar estado de colapso:", error.message);
            }
        });
    };

    // Versión móvil compacta sin auto-apertura/cierre
    if (isMobile) {
        return (
            <div className={`step-navigation-mobile ${isCollapsed ? 'collapsed' : 'expanded'}`} aria-expanded={!isCollapsed}>
                <div className="mobile-step-header" onClick={toggleCollapse} role="button" aria-label={isCollapsed ? 'Mostrar pasos' : 'Ocultar pasos'}>
                    <div className="mobile-current-step">
                        <div className="mobile-step-number-container">
                            {steps.map((step, index) => {
                                const status = getStepStatus(index, step.id);
                                return (
                                    <div
                                        key={index}
                                        className={`mobile-step-dot ${status} ${index === currentStep ? 'current' : ''}`}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onStepClick(index);
                                        }}
                                        title={step.title}
                                    >
                                        {status === 'completed' ? '✓' :
                                         status === 'error' ? '!' :
                                         index + 1}
                                    </div>
                                );
                            })}
                        </div>
                        <div className="mobile-current-title">
                            {steps[currentStep].title}
                        </div>
                    </div>
                    <div className={`collapse-icon ${isCollapsed ? 'is-collapsed' : 'is-expanded'}`} aria-hidden="true">
                        {isCollapsed ? '▾' : '▴'}
                    </div>
                </div>

                {!isCollapsed && (
                    <div className="mobile-steps-expanded">
                        {steps.map((step, index) => {
                            const status = getStepStatus(index, step.id);
                            return (
                                <div
                                    key={step.id}
                                    className={`mobile-step-item ${status}`}
                                    onClick={() => {
                                        onStepClick(index);
                                    }}
                                >
                                    <div className="mobile-step-number">
                                        {status === 'completed' && '✓'}
                                        {status === 'error' && '!'}
                                        {(status === 'active' || status === 'pending') && (index + 1)}
                                    </div>
                                    <span className="mobile-step-title">{step.title}</span>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        );
    }

    // Versión desktop
    return (
        <div className="step-navigation-sidebar">
            <div className="steps-list">
                {steps.map((step, index) => {
                    const status = getStepStatus(index, step.id);
                    return (
                        <div
                            key={step.id}
                            className={`step-item ${status}`}
                            onClick={() => onStepClick(index)}
                        >
                            <div className="step-number" aria-label={`Paso ${index + 1}`}>
                                {status === 'completed' && <span className="check-icon">✓</span>}
                                {status === 'error' && <span className="error-icon">!</span>}
                                {(status === 'active' || status === 'pending') && (index + 1)}
                            </div>
                            <div className="step-content">
                                <div className="step-title">{step.title}</div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default StepNavigation;
